import React from 'react';
import {Box, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import translate from '@/utils/translate';
import EditorCheckboxCell from '../editor-checkbox-cell';

const RATIOS = [
  {
    value: AspectRatio['1:1'],
    label: '1:1',
    descriptionLocaleKey: 'Instagram post',
    icon: (disabled: boolean) => (
      <Icon
        name="Features-Format-Square"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: AspectRatio['9:16'],
    label: '9:16',
    descriptionLocaleKey: 'Tiktok, Instagram story',
    icon: (disabled: boolean) => (
      <Icon
        name="Features-Format-Portrait"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: AspectRatio['16:9'],
    label: '16:9',
    descriptionLocaleKey: 'YouTube',
    icon: (disabled: boolean) => (
      <Icon
        name="Features-Format-Landscape"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: AspectRatio['4:3'],
    label: '4:3',
    descriptionLocaleKey: 'YouTube',
    icon: (disabled: boolean) => (
      <Icon
        name="Features-Format-4x3"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
  {
    value: AspectRatio['4:5'],
    label: '4:5',
    descriptionLocaleKey: 'Instagram Feed Portrait',
    icon: (disabled: boolean) => (
      <Icon
        name="Features-Format-4x5"
        size="medium"
        appearance={disabled ? 'disabled' : 'black'}
        color={disabled ? designSystemToken('semantic.fg.disabled') : undefined}
      />
    ),
  },
];

const ChooseRatiosContainer = styled(Box)(() => ({
  width: '100%',
  padding: '24px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  height: '100%',
}));

const RatioGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(3, 1fr)',
  gap: '20px',
  width: '100%',
  height: '100%',
}));

interface ChooseRatiosProps {
  selected: AspectRatio[];
  onSelectionChange: (selected: AspectRatio[]) => void;
  disabledAspectRatio?: AspectRatio;
  tooltip: string;
}

function ChooseRatios({
  selected,
  onSelectionChange,
  disabledAspectRatio,
  tooltip,
}: ChooseRatiosProps) {
  const onCellClick = (key: AspectRatio) => {
    if (selected.includes(key)) {
      onSelectionChange(selected.filter((k) => k !== key));
    } else {
      onSelectionChange([...selected, key]);
    }
  };

  return (
    <ChooseRatiosContainer>
      <Headline
        size="md"
        color={designSystemToken('semantic.fg.primary')}
        sx={{width: '100%', textAlign: 'left'}}
      >
        {translate('Choose aspect ratios')}
      </Headline>
      <RatioGrid>
        {RATIOS.map((ratio) => {
          const disabled = disabledAspectRatio === ratio.value;
          return (
            <EditorCheckboxCell
              key={ratio.value}
              isDisabled={disabled}
              isSelected={selected.includes(ratio.value) && !disabled}
              onClick={() => onCellClick(ratio.value)}
              icon={ratio.icon(disabled)}
              label={ratio.label}
              description={translate(ratio.descriptionLocaleKey)}
              tooltip={tooltip}
            />
          );
        })}
      </RatioGrid>
    </ChooseRatiosContainer>
  );
}

export default ChooseRatios;
